'use client';

import { useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ArrowRight, Check, Mail, Sparkles } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

interface AuthModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PERKS = [
  '7 days at full Enterprise capability',
  'No credit card required',
  'Unlimited dispatchers on every plan',
];

export default function AuthModal({ open, onOpenChange }: AuthModalProps) {
  const router = useRouter();
  const [email, setEmail] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    onOpenChange(false);
    router.push(`/signup?email=${encodeURIComponent(email)}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="theme-playful max-w-3xl overflow-hidden border-2 border-pop-ink p-0 shadow-pop-lg">
        <DialogTitle className="sr-only">Create your Logistr account</DialogTitle>

        <div className="flex flex-col md:flex-row">
          {/* Action side */}
          <div className="flex w-full flex-col justify-center bg-card p-8 md:w-1/2 md:p-10">
            <h2 className="font-outfit text-3xl font-extrabold leading-tight text-foreground">
              Start your first mission
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Enter your email to create an account in under a minute.
            </p>

            <form onSubmit={handleSubmit} className="mt-7 space-y-3">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" strokeWidth={2.5} />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@example.com"
                  className="w-full rounded-full border-2 border-pop-ink bg-white py-3 pl-10 pr-4 text-sm font-medium text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-1"
                />
              </div>
              <Button variant="candy" size="pill" className="w-full" type="submit">
                Get started
                <span className="ml-auto flex h-6 w-6 items-center justify-center rounded-full bg-white">
                  <ArrowRight className="h-3.5 w-3.5 text-accent" strokeWidth={3} />
                </span>
              </Button>
            </form>

            <p className="mt-5 text-center text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link
                href="/login"
                onClick={() => onOpenChange(false)}
                className="font-bold text-accent hover:underline"
              >
                Sign in
              </Link>
            </p>

            <p className="mt-6 border-t-2 border-dashed border-border pt-4 text-center text-xs leading-relaxed text-muted-foreground">
              By continuing you agree to our{' '}
              <Link href="/terms-of-service" className="font-bold underline">Terms</Link> and{' '}
              <Link href="/privacy-policy" className="font-bold underline">Privacy Policy</Link>.
            </p>
          </div>

          {/* Decorative side */}
          <div className="relative hidden w-1/2 overflow-hidden border-l-2 border-pop-ink bg-accent p-10 md:block">
            <div className="relative flex h-full flex-col justify-center text-white">
              <span className="inline-flex w-fit items-center gap-2 rounded-full border-2 border-pop-ink bg-pop-amber px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-wider text-pop-ink">
                <Sparkles className="h-3.5 w-3.5" strokeWidth={2.5} />
                Free for 7 days
              </span>

              <h3 className="mt-6 font-outfit text-2xl font-extrabold leading-tight">
                Everyone on the same mission
              </h3>

              <ul className="mt-6 space-y-3">
                {PERKS.map((perk) => (
                  <li key={perk} className="flex items-start gap-2.5 text-sm">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 border-pop-ink bg-pop-mint">
                      <Check className="h-2.5 w-2.5 text-pop-ink" strokeWidth={4} />
                    </span>
                    <span className="text-white/90">{perk}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
